/**
 * The first screen a new parent sees, and it asks one question.
 *
 * "Do you have a family code?" No, and they type a family name and land in
 * the valley. Yes, and they paste it, confirm which family it is, and land in
 * the valley. Nothing is shown once, nothing has to be written down, and the
 * family's name is asked for exactly once.
 *
 * What used to be here: a generated four-word key phrase, a paragraph
 * explaining that it could never be shown again, and a checkbox saying "I
 * have written it down" -- on BOTH paths, and then a setup screen asking for
 * the family name a second time. The phrase was the part nobody keeps, and
 * every parent had a different one, so it could not be shared or asked for.
 * DECISIONS.md section 18 has the rest of the argument.
 */

import { useState } from 'react';
import {
  createFamily,
  joinFamily,
  previewFamily,
  type FamilyPreview,
} from '../family/families';

type Mode = 'choose' | 'create' | 'join' | 'confirm';

export function ChooseFamily({
  onReady,
}: {
  onReady: (familyId: string, key: CryptoKey, newCode?: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<FamilyPreview | null>(null);

  async function attempt(work: () => Promise<void>): Promise<void> {
    setError('');
    setBusy(true);
    try {
      await work();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'choose') {
    return (
      <>
        <h2>Set up your family</h2>
        <p>
          Your family&rsquo;s names and characters are encrypted on this device
          before they are stored, with a family key only you hold. Nobody at
          Birbly, and nobody at Amazon, can read them.
        </p>
        <p className="field-label">Do you have a family code?</p>
        <div className="actions">
          <button className="primary" onClick={() => setMode('join')}>
            Yes, I have one
          </button>
          <button onClick={() => setMode('create')}>No, start a new family</button>
        </div>
        <p className="footnote">
          A family code is what another parent in your family would send you.
          One code per family, and any member can find it under Settings.
        </p>
      </>
    );
  }

  if (mode === 'confirm' && preview) {
    return (
      <>
        <h2>{preview.family ? `Join ${preview.family}?` : 'Join this family?'}</h2>
        <p>
          Joining lets you see this family&rsquo;s money, goals and journal, so
          it is worth a second look before you do.
        </p>

        {!preview.family ? (
          <p className="footnote">
            This family has not set a name yet, so there is nothing to check
            against. If you were not expecting this code, ask the parent who
            sent it.
          </p>
        ) : null}

        {error ? <p className="error">{error}</p> : null}

        <div className="actions">
          <button aria-busy={busy}
            className="primary"
            disabled={busy}
            onClick={() =>
              void attempt(async () => {
                const joined = await joinFamily(code);
                onReady(joined.id, joined.key);
              })
            }
          >
            {busy ? 'Working…' : 'Yes, join it'}
          </button>
          <button
            disabled={busy}
            onClick={() => {
              setMode('join');
              setPreview(null);
              setError('');
            }}
          >
            Not this one
          </button>
        </div>
      </>
    );
  }

  const creating = mode === 'create';

  return (
    <>
      <h2>{creating ? 'Start a new family' : 'Join your family'}</h2>

      {creating ? (
        <label className="field">
          <span>What is your family called?</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={50}
            autoComplete="off"
            placeholder="The Smiths"
          />
          <small>Encrypted before it leaves this device.</small>
        </label>
      ) : (
        <label className="field">
          <span>Family code</span>
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="K7QM-3XR9-TVWX-2345-P8NB"
          />
          <small>
            From a parent already in your family. Capitals and dashes do not
            matter.
          </small>
        </label>
      )}

      {error ? <p className="error">{error}</p> : null}

      <div className="actions">
        <button aria-busy={busy}
          className="primary"
          disabled={busy || (creating ? !name.trim() : !code.trim())}
          onClick={() =>
            void attempt(async () => {
              if (creating) {
                const made = await createFamily(name.trim());
                // The code goes to the valley with them, so it can be shown
                // once in a banner there rather than behind a checkbox here.
                onReady(made.id, made.key, made.code);
              } else {
                // Check what the code is FOR before spending it. Nothing is
                // joined until the next screen is confirmed.
                setPreview(await previewFamily(code));
                setMode('confirm');
              }
            })
          }
        >
          {busy ? 'Working…' : creating ? 'Create it' : 'Continue'}
        </button>
        <button
          disabled={busy}
          onClick={() => {
            setMode('choose');
            setError('');
          }}
        >
          Back
        </button>
      </div>
    </>
  );
}
